import { Button } from './Button';
import { cn } from './cn';

/**
 * Previous, next, and where you are.
 *
 * Both applications had one, neither knew about the other, and the product's
 * printed "Page 2 of 9" twice — once on the left and again between the buttons.
 * The console's said "Showing 21 to 40 of 173 items", which is the more useful
 * sentence, so that is the one kept; the caller supplies it, because the words
 * are words and this package does not translate.
 *
 * Hidden entirely at one page: a control that cannot do anything is noise.
 */

export interface PaginationProps {
  page: number;
  totalPages: number;
  /** The sentence naming the range on show, e.g. "21–40 of 173 vehicles". */
  summary?: string;
  previousLabel: string;
  nextLabel: string;
  onPageChange: (page: number) => void;
  /** Stops both buttons while a page is on its way. */
  isLoading?: boolean;
  className?: string;
}

export function Pagination({
  page,
  totalPages,
  summary,
  previousLabel,
  nextLabel,
  onPageChange,
  isLoading = false,
  className,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <div className={cn('flex items-center justify-between gap-4 px-2 py-3', className)}>
      <p className="text-xs text-muted-foreground">{summary}</p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1 || isLoading}
        >
          {previousLabel}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages || isLoading}
        >
          {nextLabel}
        </Button>
      </div>
    </div>
  );
}

/** The range a page covers, for the summary sentence. */
export function pageRange(page: number, limit: number, total: number) {
  const first = total === 0 ? 0 : (page - 1) * limit + 1;
  return { first, last: Math.min(page * limit, total) };
}
